import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { API, formatApiErrorDetail } from "../lib/api";

const SERVICES = [
  { value: "ersteinschaetzung", label: "01 — Ersteinschätzung (CHF 150.–*)" },
  { value: "bauermittlung", label: "02 — Bauermittlung (CHF 390.–*)" },
  { value: "komplettbegleitung", label: "03 — Komplettbegleitung (Individuell*)" },
  { value: "andere", label: "Andere / Sonderfall" },
];

export default function ProjectRequestDialog({ open, onOpenChange, defaultService = "ersteinschaetzung" }) {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    service_type: defaultService,
    description: "",
  });
  const [file, setFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("name", form.name);
      fd.append("email", form.email);
      fd.append("phone", form.phone);
      fd.append("service_type", form.service_type);
      fd.append("description", form.description);
      if (file) fd.append("file", file);

      const token = localStorage.getItem("lb_token");
      const res = await fetch(`${API}/project-request`, {
        method: "POST",
        body: fd,
        credentials: "include",
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(formatApiErrorDetail(data.detail) || `HTTP ${res.status}`);
      }
      toast.success("Projektanfrage gesendet", {
        description: "Wir prüfen Ihre Unterlagen und melden uns zeitnah.",
      });
      setForm({ name: "", email: "", phone: "", service_type: defaultService, description: "" });
      setFile(null);
      onOpenChange(false);
    } catch (err) {
      toast.error("Fehler", { description: err.message });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="letsbau-dialog max-w-xl max-h-[90vh] overflow-y-auto" data-testid="project-dialog">
        <DialogHeader>
          <DialogTitle className="text-neutral-900 text-2xl font-light tracking-tight">Projekt anfragen</DialogTitle>
          <DialogDescription className="text-neutral-500">
            Beschreiben Sie Ihr Bauvorhaben. Optional können Sie Pläne (PDF, JPG, PNG — max. 15 MB) hochladen.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="grid gap-4 mt-2">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="grid gap-2">
              <Label htmlFor="p-name">Name</Label>
              <Input id="p-name" data-testid="project-name-input" required value={form.name} onChange={update("name")} />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="p-email">E-Mail</Label>
              <Input id="p-email" data-testid="project-email-input" type="email" required value={form.email} onChange={update("email")} />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="grid gap-2">
              <Label htmlFor="p-phone">Telefon (optional)</Label>
              <Input id="p-phone" data-testid="project-phone-input" value={form.phone} onChange={update("phone")} />
            </div>
            <div className="grid gap-2">
              <Label>Leistung</Label>
              <Select value={form.service_type} onValueChange={(v) => setForm((f) => ({ ...f, service_type: v }))}>
                <SelectTrigger data-testid="project-service-select" className="bg-neutral-50 border-neutral-200 text-neutral-900">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-white text-neutral-900 border-neutral-200">
                  {SERVICES.map((s) => (
                    <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid gap-2">
            <Label htmlFor="p-desc">Projektbeschreibung</Label>
            <Textarea id="p-desc" data-testid="project-description-input" rows={5} required value={form.description} onChange={update("description")} />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="p-file">Plan / Dokument (optional)</Label>
            <input
              id="p-file"
              data-testid="project-file-input"
              type="file"
              accept=".pdf,.jpg,.jpeg,.png,.dwg,.doc,.docx"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              className="text-sm text-neutral-700 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:bg-neutral-900 file:text-white hover:file:bg-neutral-700 cursor-pointer"
            />
            {file && <div className="text-xs text-neutral-500">Ausgewählt: {file.name} ({Math.round(file.size / 1024)} KB)</div>}
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" className="lbtn" onClick={() => onOpenChange(false)} data-testid="project-cancel-btn">
              Abbrechen
            </button>
            <button type="submit" className="lbtn primary" disabled={submitting} data-testid="project-submit-btn">
              {submitting ? "Senden..." : "Anfrage senden"}
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
